import { FilePdfOutlined, LinkOutlined, MailOutlined } from "@ant-design/icons"
import {
  Button,
  Card,
  Dropdown,
  Flex,
  type MenuProps,
  Space,
  Tag,
  Typography
} from "antd"
import React, { useMemo } from "react"
import { Link } from "react-router-dom"

import Icon from "../../../../Icon"
import { empty } from "../../../../constants/placeholders"
import { RoutesPaths } from "../../../../constants/routes.ts"
import { useAppDispatch } from "../../../../hooks/use-app-dispatch.ts"
import { cn } from "../../../../utils/cn.ts"
import { getDeclineReasonForDisplay } from "../../../../utils/documents/document-decline-outcome.ts"
import { formatNumber } from "../../../../utils/number/formate.ts"
import {
  CONTRACT_SHARE_EMAIL_STATUSES,
  CONTRACT_STATUSES_OBJ
} from "../../constants/contract-statuses-keys.ts"
import { emailContractToClient } from "../../redux/contracts.thunks.ts"
import { type ContractListItem } from "../../types/types.ts"

const { Title, Text } = Typography

interface ContractsSidebarListItemProps {
  isActive?: boolean
  viewLeadAllowed?: boolean
  onSelect: (id: string) => void
  contract: ContractListItem
}

export function ContractsSidebarListItem({
  isActive,
  viewLeadAllowed,
  onSelect,
  contract
}: ContractsSidebarListItemProps) {
  const dispatch = useAppDispatch()
  const submittedAttachment = contract?.attachments?.find(
    (c) => c?.is_submitted
  )
  const date = contract?.reviewed_at || contract?.submitted_for_review_at
  const version = submittedAttachment?.full_version || contract?.version_display
  const statusData = CONTRACT_STATUSES_OBJ?.[contract?.status]
  const displayReason = getDeclineReasonForDisplay(contract?.decline_reason)

  const onClickHandler = () => onSelect(contract?.id)

  const kebabMenuItems: MenuProps["items"] = useMemo(() => {
    const list = []

    if (CONTRACT_SHARE_EMAIL_STATUSES.includes(contract?.status)) {
      list.push({
        key: "1",
        label: "Share via Email",
        onClick: () => dispatch(emailContractToClient(contract.id)),
        icon: <MailOutlined />
      })
    }

    if (viewLeadAllowed && contract?.lead_id) {
      list.push({
        key: "2",
        label: (
          <Link target={"_blank"} to={RoutesPaths.lead.view(contract.lead_id)}>
            Open Lead
          </Link>
        ),
        icon: <LinkOutlined />
      })
    }

    return list
  }, [
    dispatch,
    viewLeadAllowed,
    contract.id,
    contract.lead_id,
    contract?.contract_id,
    contract?.status
  ])

  return (
    <Card
      size={"small"}
      onClick={onClickHandler}
      className={cn(
        "mx-0.5 mb-3 transition-colors duration-300 hover:bg-primary/5 cursor-pointer border-primary/50",
        isActive && "border-primary outline outline-primary bg-primary/5"
      )}
    >
      <Flex justify={"space-between"} className={"pb-3"}>
        <div>
          <Title
            level={5}
            ellipsis={{ rows: 2, tooltip: contract?.contract_id }}
          >
            {contract?.contract_id}
          </Title>
          <Flex vertical>
            <Text type={"secondary"}>{contract?.customer_name}</Text>
            <Text type={"secondary"}>{contract?.lead_name}</Text>
          </Flex>
        </div>
        <Flex vertical align={"end"} gap={"small"}>
          <Tag style={statusData?.style} className={"m-0"}>
            {statusData?.label}
          </Tag>
          {kebabMenuItems.length > 0 ? (
            <div onClick={(e) => e.stopPropagation()}>
              <Dropdown
                menu={{ items: kebabMenuItems }}
                trigger={["click"]}
                placement="bottom"
              >
                <Button type={"text"} icon={<Icon type={"VerticalDots"} />} />
              </Dropdown>
            </div>
          ) : null}
        </Flex>
      </Flex>
      {displayReason ? (
        <Text type="secondary" className="text-xs line-clamp-2 mb-2 block">
          {displayReason}
        </Text>
      ) : null}
      <Flex justify={"space-between"}>
        <Space>
          <Text className={"text-xs inline-flex items-center gap-1"}>
            <Icon type={"Calendar"} />
            {date || empty}
          </Text>
          {version && (
            <Text className={"text-xs inline-flex items-center gap-1"}>
              <FilePdfOutlined />
              {version}
            </Text>
          )}
        </Space>
        <Text>{formatNumber(contract?.total_amount)}</Text>
      </Flex>
    </Card>
  )
}
